import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { MapPin, Search, Building2, Globe } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { __resolve } from '@/lib/i18n';

export interface LocationData {
  place_id: string;
  formatted_address: string;
  lat: number;
  lon: number;
  city: string | null;
  region: string | null;
  country: string | null;
  country_iso2: string | null;
  postal_code: string | null;
  location_type: 'city' | 'country' | 'region';
}

export interface LocationSuggestion {
  place_id: string;
  description: string;
  main_text: string;
  secondary_text: string;
  types: string[];
  priority?: 'city' | 'country' | 'address' | 'region';
}

export interface LocationAutocompleteProps {
  value?: string;
  onChange?: (value: string) => void;
  onLocationSelect?: (location: LocationData) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: string;
  types?: 'cities' | 'regions' | 'address' | 'establishment'; // Default: cities. 'regions' searches for administrative areas like Tuscany, Provence, etc.
}

export function LocationAutocomplete({
  value = '',
  onChange,
  onLocationSelect,
  placeholder = __resolve('ui::Search for a city or address...'),
  className,
  disabled = false,
  error,
  types = 'cities'
}: LocationAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [justSelected, setJustSelected] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Debounced search
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Don't search if we just selected something
    if (justSelected) {
      setJustSelected(false);
      setIsDebouncing(false);
      return;
    }

    if (value.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      setIsDebouncing(false);
      return;
    }

    // Show debouncing indicator for region searches
    if (types === 'regions' && value.length >= 2) {
      setIsDebouncing(true);
    }

    timeoutRef.current = setTimeout(() => {
      setIsDebouncing(false);
      searchPlaces(value);
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsDebouncing(false);
    };
  }, [value, types, justSelected]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchPlaces = async (query: string) => {
    if (query.length < 2) return;

    setIsLoading(true);
    try {
      // Smart search strategy: search based on types prop
      let allSuggestions: LocationSuggestion[] = [];
      
      if (types === 'regions') {
        // Search for regions (administrative areas like Tuscany, Provence)
        const [regionResponse, countryResponse] = await Promise.all([
          apiFetch(`/api/places/autocomplete?input=${encodeURIComponent(query)}&types=(regions)`),
          apiFetch(`/api/places/autocomplete?input=${encodeURIComponent(query)}&types=country`)
        ]);

        if (regionResponse.ok) {
          const regionData = await regionResponse.json();
          allSuggestions.push(...regionData.data.suggestions.map((s: LocationSuggestion) => ({
            ...s,
            priority: 'city' as const // Reuse 'city' priority styling for regions
          })));
        }

        if (countryResponse.ok) {
          const countryData = await countryResponse.json();
          const countrySuggestions = countryData.data.suggestions.filter((c: LocationSuggestion) =>
            !allSuggestions.some(existing => existing.place_id === c.place_id)
          );
          allSuggestions.push(...countrySuggestions.map((s: LocationSuggestion) => ({
            ...s,
            priority: 'country' as const
          })));
        }
      } else if (types === 'establishment') {
        // For establishments, search both establishments AND addresses
        // This allows users to search for hotels or street addresses
        const [establishmentResponse, addressResponse] = await Promise.all([
          apiFetch(`/api/places/autocomplete?input=${encodeURIComponent(query)}&types=establishment`),
          apiFetch(`/api/places/autocomplete?input=${encodeURIComponent(query)}&types=address`)
        ]);
        
        // Add establishment results first (higher priority for location search)
        if (establishmentResponse.ok) {
          const establishmentData = await establishmentResponse.json();
          allSuggestions.push(...establishmentData.data.suggestions.map((s: LocationSuggestion) => ({
            ...s,
            priority: 'address' // Use 'address' priority for establishments
          })));
        }
        
        // Add address results (for street addresses that aren't establishments)
        if (addressResponse.ok) {
          const addressData = await addressResponse.json();
          // Filter out addresses that are duplicates of establishments
          const addressSuggestions = addressData.data.suggestions.filter((addr: LocationSuggestion) => 
            !allSuggestions.some(existing => existing.place_id === addr.place_id)
          );
          allSuggestions.push(...addressSuggestions.map((s: LocationSuggestion) => ({
            ...s,
            priority: 'address' // Mark as address for sorting
          })));
        }
      } else {
        // For cities or address, use the existing multi-search strategy
        // Also search (regions) to include states/provinces (administrative_area_level_1)
        const [cityResponse, regionResponse, countryResponse, addressResponse] = await Promise.all([
          apiFetch(`/api/places/autocomplete?input=${encodeURIComponent(query)}&types=(cities)`),
          apiFetch(`/api/places/autocomplete?input=${encodeURIComponent(query)}&types=(regions)`),
          apiFetch(`/api/places/autocomplete?input=${encodeURIComponent(query)}&types=country`),
          apiFetch(`/api/places/autocomplete?input=${encodeURIComponent(query)}&types=address`)
        ]);

        // Add city results first (highest priority)
        if (cityResponse.ok) {
          const cityData = await cityResponse.json();
          allSuggestions.push(...cityData.data.suggestions.map((s: LocationSuggestion) => ({
            ...s,
            priority: 'city' as const,
          })));
        }

        // Add region/state results (states, provinces — from (regions) search)
        if (regionResponse.ok) {
          const regionData = await regionResponse.json();
          const regionSuggestions = regionData.data.suggestions.filter((r: LocationSuggestion) =>
            !allSuggestions.some(existing => existing.place_id === r.place_id)
          );
          allSuggestions.push(...regionSuggestions.map((s: LocationSuggestion) => ({
            ...s,
            priority: 'region' as const,
          })));
        }

        // Add country results
        if (countryResponse.ok) {
          const countryData = await countryResponse.json();
          // Filter out countries that are duplicates
          const countrySuggestions = countryData.data.suggestions.filter((country: LocationSuggestion) =>
            !allSuggestions.some(existing => existing.place_id === country.place_id)
          );
          allSuggestions.push(...countrySuggestions.map((s: LocationSuggestion) => ({
            ...s,
            priority: 'country' // Mark as country for sorting
          })));
        }
        
        // Add address results after cities and countries (if types === 'address')
        if (types === 'address' && addressResponse.ok) {
          const addressData = await addressResponse.json();
          // Filter out addresses that are duplicates of cities/countries
          const addressSuggestions = addressData.data.suggestions.filter((addr: LocationSuggestion) => 
            !allSuggestions.some(existing => existing.place_id === addr.place_id)
          );
          allSuggestions.push(...addressSuggestions.map((s: LocationSuggestion) => ({
            ...s,
            priority: 'address' // Mark as address for sorting
          })));
        }
      }
      
      // Sort: countries first, then regions/states, then cities, then addresses
      allSuggestions.sort((a, b) => {
        const priorityOrder = { country: 1, region: 2, city: 3, address: 4 };
        const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] || 99;
        const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] || 99;
        if (aPriority !== bPriority) return aPriority - bPriority;
        return 0; // Keep Google's relevance order within same type
      });
      
      // Limit to 10 results for better UX (increased from 8 to accommodate countries)
      setSuggestions(allSuggestions.slice(0, 10));
      setIsOpen(true);
      setHighlightedIndex(-1);
      
    } catch (error) {
      console.error('Places search failed:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const selectSuggestion = async (suggestion: LocationSuggestion) => {
    setIsLoading(true);
    setIsOpen(false);
    setJustSelected(true); // Prevent search from retriggering
    
    try {
      // Get detailed place information
      const response = await apiFetch(`/api/places/details?place_id=${suggestion.place_id}`);
      
      if (response.ok) {
        const data = await response.json();
        const locationData: LocationData = data.data;
        
        // Clear the search input after selection to prevent retriggering
        onChange?.('');
        
        // Clear suggestions and close dropdown
        setSuggestions([]);
        setHighlightedIndex(-1);
        
        // Callback with full location data
        onLocationSelect?.(locationData);
      }
    } catch (error) {
      console.error('Failed to get place details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange?.(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          selectSuggestion(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <div className={cn('relative', className)}>
      <div className="relative">
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            // Only show dropdown if we have recent suggestions and haven't just selected
            if (value.length >= 2 && suggestions.length > 0 && !justSelected) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          leftIcon={isLoading ? (
            <Spinner size="sm" />
          ) : (
            <Search className="w-4 h-4 text-muted-foreground" />
          )}
        />
      </div>

      {/* Loading indicator - show when searching for regions */}
      {(isLoading || isDebouncing) && value.length >= 2 && types === 'regions' && (
        <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner size="sm" />
          <span>{__resolve('ui::Searching for regions...')}</span>
        </div>
      )}

      {/* Dropdown - z-index must be higher than modal (z-modal: 1200) */}
      {isOpen && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-[1300] w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {suggestions.map((suggestion, index) => {
            const isCity = suggestion.priority === 'city';
            const isCountry = suggestion.priority === 'country';
            const isRegion = suggestion.priority === 'region';
            return (
              <button
                key={suggestion.place_id}
                type="button"
                className={cn(
                  'w-full px-3 py-2 text-left hover:bg-accent focus:bg-accent focus:outline-none',
                  'flex items-start gap-2 border-b border-border last:border-b-0',
                  index === highlightedIndex && 'bg-primary-50',
                  isCity && 'bg-blue-50 border-l-2 border-l-blue-400', // Highlight cities
                  isRegion && 'bg-purple-50 border-l-2 border-l-purple-400', // Highlight states/regions
                  isCountry && 'bg-green-50 border-l-2 border-l-green-400' // Highlight countries
                )}
                onClick={() => selectSuggestion(suggestion)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {isCity ? (
                  <Building2 className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                ) : isRegion ? (
                  <MapPin className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                ) : isCountry ? (
                  <Globe className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                ) : (
                  <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className={cn(
                    "font-medium truncate",
                    isCity ? "text-blue-900" : isRegion ? "text-purple-900" : isCountry ? "text-green-900" : "text-foreground"
                  )}>
                    {suggestion.main_text}
                    {isCity && <span className="text-xs text-blue-600 ml-2">{__resolve('ui::CITY')}</span>}
                    {isRegion && <span className="text-xs text-purple-600 ml-2">{__resolve('ui::STATE')}</span>}
                    {isCountry && <span className="text-xs text-green-600 ml-2">{__resolve('ui::COUNTRY')}</span>}
                  </div>
                  {suggestion.secondary_text && (
                    <div className="text-sm text-muted-foreground truncate">
                      {suggestion.secondary_text}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}